// 认证管理器 - 用于处理token过期等全局认证状态
import api from './axios';

class AuthManager {
  constructor() {
    this.forceLogoutCallback = null;
    this.tokenRefreshTimer = null;
    this.refreshThreshold = 3600; // 1小时，当token剩余时间小于1小时时自动续期
  }

  // 兼容 base64url 且保留 UTF-8 字符的 JWT 解析
  decodeJwtPayloadUtf8(token) {
    try {
      const part = token.split('.')[1];
      if (!part) return null;
      const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
      const pad = '='.repeat((4 - (b64.length % 4)) % 4);
      const base64 = b64 + pad;
      const binary = atob(base64);
      // 优先使用 TextDecoder
      try {
        const bytes = Uint8Array.from(binary, ch => ch.charCodeAt(0));
        const jsonStr = new TextDecoder('utf-8').decode(bytes);
        return JSON.parse(jsonStr);
      } catch (_) {
        // 退回 escape/encode 方案
        const jsonStr = decodeURIComponent(escape(binary));
        return JSON.parse(jsonStr);
      }
    } catch (_) {
      return null;
    }
  }

  // 设置强制登出的回调函数
  setForceLogoutCallback(callback) {
    this.forceLogoutCallback = callback;
  }

  // 执行强制登出
  forceLogout() {
    // 清除本地存储
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('permissions');
    
    // 清除定时器
    this.clearTokenRefreshTimer();
    
    // 如果有回调函数，执行它
    if (this.forceLogoutCallback) {
      this.forceLogoutCallback();
    } else {
      // 备用方案：直接跳转
      if (typeof window !== 'undefined' && window.location?.pathname !== '/mobile/call') {
        window.location.href = '/';
      }
    }
  }

  // 检查token是否有效
  isTokenValid() {
    const token = localStorage.getItem('token');
    if (!token) return false;
    
    try {
      // 解析JWT token
      const payload = this.decodeJwtPayloadUtf8(token);
      if (!payload) return false;
      
      const now = Math.floor(Date.now() / 1000);
      
      // 检查token是否过期
      if (payload.exp && payload.exp < now) {
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Token解析错误:', error);
      return false;
    }
  }

  // 检查token是否即将过期
  isTokenExpiringSoon() {
    const token = localStorage.getItem('token');
    if (!token) return true;
    
    try {
      // 解析JWT token
      const payload = this.decodeJwtPayloadUtf8(token);
      if (!payload) return true;
      
      const now = Math.floor(Date.now() / 1000);
      
      // 检查token是否即将过期（剩余时间小于阈值）
      if (payload.exp && (payload.exp - now) < this.refreshThreshold) {
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Token解析错误:', error);
      return true;
    }
  }

  // 获取token过期时间
  getTokenExpirationTime() {
    const token = localStorage.getItem('token');
    if (!token) return 0;
    
    try {
      // 解析JWT token
      const payload = this.decodeJwtPayloadUtf8(token);
      return payload?.exp || 0;
    } catch (error) {
      console.error('Token解析错误:', error);
      return 0;
    }
  }

  // 清除token刷新定时器
  clearTokenRefreshTimer() {
    if (this.tokenRefreshTimer) {
      clearInterval(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }
  }

  // 启动token自动刷新监控
  startTokenRefreshMonitor() {
    // 清除之前的定时器
    this.clearTokenRefreshTimer();
    
    // 每30秒检查一次token状态
    this.tokenRefreshTimer = setInterval(() => {
      this.checkTokenStatus();
    }, 30000);
  }

  // 检查token状态并处理
  checkTokenStatus() {
    // 检查是否有token
    const token = localStorage.getItem('token');
    if (!token) {
      // 没有token，说明用户还没有登录，不需要执行强制登出操作
      return;
    }
    
    // 检查token是否有效
    if (!this.isTokenValid()) {
      console.warn('Token已过期，正在跳转到登录页面...');
      this.forceLogout();
      return;
    }
    
    // 检查token是否即将过期
    if (this.isTokenExpiringSoon()) {
      console.log('Token即将过期，准备自动续期...');
      this.refreshToken();
    }
  }

  // 刷新token
  async refreshToken() {
    try {
      // 发送刷新token请求
      const response = await api.post('/auth/refresh');
      
      if (response.data && response.data.success && response.data.token) {
        // 更新本地存储的token
        localStorage.setItem('token', response.data.token);
        console.log('✅ Token自动续期成功');
      } else {
        console.warn('⚠️ Token续期失败，可能需要重新登录');
        // 不要立即强制登出，先检查当前token是否仍然有效
        // 只有当token确实过期时才登出
        if (!this.isTokenValid()) {
          this.forceLogout();
        }
      }
    } catch (error) {
      console.error('❌ Token续期出错:', error);
      // 出错时不要立即登出，先检查当前token是否仍然有效
      // 只有当token确实过期时才登出
      if (!this.isTokenValid()) {
        this.forceLogout();
      }
    }
  }
} 

// 创建单例实例
const authManager = new AuthManager();

export default authManager;
